import { useState } from 'react';
import Card from 'components/card';
import { MdPeople, MdPerson, MdSave } from 'react-icons/md';

// Custom CSS for the sliders
const sliderStyles = `
  .slider::-webkit-slider-thumb {
    appearance: none;
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }
  
  .slider::-moz-range-thumb {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }

  .slider-mentorship::-webkit-slider-thumb {
    appearance: none;
    height: 24px;
    width: 24px;
    border-radius: 50%;
    background: #6b46c1;
    cursor: pointer;
    border: 3px solid #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
  
  .slider-mentorship::-moz-range-thumb {
    height: 24px;
    width: 24px;
    border-radius: 50%;
    background: #6b46c1;
    cursor: pointer;
    border: 3px solid #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

interface MentorshipSettings {
  maxMenteesPerMentor: number;
  sessionDuration: number;
  enableAutoMatching: boolean;
  allowMultipleMentors: boolean;
}

const MentorshipSettingsPage = () => {
  const [settings, setSettings] = useState<MentorshipSettings>({
    maxMenteesPerMentor: 1,
    sessionDuration: 60,
    enableAutoMatching: true,
    allowMultipleMentors: false
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleSaveSettings = async () => {
    setIsSaving(true);
    // Simulate API call
    setTimeout(() => {
      console.log('Settings saved:', settings);
      setIsSaving(false);
    }, 1500);
  };

  const updateSettings = (key: keyof MentorshipSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="mt-3 h-full w-full">
      <style dangerouslySetInnerHTML={{ __html: sliderStyles }} />
      <Card extra="w-full pb-10 p-6 h-full">
        <div className="space-y-8">
          {/* Mentorship Types */}
          <div>
            <h5 className="text-lg font-semibold text-navy-700 dark:text-white mb-4">
              Mentorship Types
            </h5>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <label className="block text-sm font-medium text-navy-700 dark:text-white mb-4">
                Mentees per Mentor Limit
              </label>
              
              <div className="space-y-4">
                {/* Current Status */}
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                      settings.maxMenteesPerMentor === 1 
                        ? 'bg-brand-500' 
                        : 'bg-gray-600'
                    }`}>
                      {settings.maxMenteesPerMentor === 1 ? (
                        <MdPerson className="h-5 w-5 text-white" />
                      ) : (
                        <MdPeople className="h-5 w-5 text-white" />
                      )}
                    </div>
                    <div>
                      <h6 className="font-semibold text-navy-700 dark:text-white">
                        {settings.maxMenteesPerMentor === 1 ? '1:1 Mentorship' : '1:Many Mentorship'}
                      </h6>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {settings.maxMenteesPerMentor === 1 
                          ? 'One mentor, one mentee' 
                          : `Up to ${settings.maxMenteesPerMentor} mentees per mentor`
                        }
                      </p>
                    </div>
                  </div>
                  
                  {/* Information block on right side - taking up 70% of remaining width */}
                  {settings.maxMenteesPerMentor === 1 ? (
                    <div className="bg-brand-50 dark:bg-brand-900/10 rounded-lg p-3 flex-grow">
                      <div className="flex items-start gap-2">
                        <MdPerson className="h-4 w-4 text-brand-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-brand-700 dark:text-brand-300 mb-1">
                            Personal Focus
                          </p>
                          <div className="text-xs text-brand-600 dark:text-brand-400">
                            Deep relationships • Individual attention • Tailored guidance • Requires equal mentor-mentee numbers • Success depends on both parties' commitment
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-3 flex-grow">
                      <div className="flex items-start gap-2">
                        <MdPeople className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-green-700 dark:text-green-300 mb-1">
                            Group Efficiency
                          </p>
                          <div className="text-xs text-green-600 dark:text-green-400">
                            Scalable approach • Peer learning • Resource efficiency
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Slider */}
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Mentees per Mentor</span>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-brand-500">{settings.maxMenteesPerMentor}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {settings.maxMenteesPerMentor === 1 ? 'mentee' : 'mentees'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="relative">
                      <input
                        type="range"
                        min="1"
                        max="10"
                        step="0.1"
                        value={settings.maxMenteesPerMentor}
                        onChange={(e) => updateSettings('maxMenteesPerMentor', Math.round(parseFloat(e.target.value)))}
                        className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-600 slider"
                        style={{
                          background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${((settings.maxMenteesPerMentor - 1) / 9) * 100}%, #E5E7EB ${((settings.maxMenteesPerMentor - 1) / 9) * 100}%, #E5E7EB 100%)`
                        }}
                      />
                      <div className="flex justify-between mt-2 px-1">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                          <span 
                            key={num}
                            className={`text-xs cursor-pointer ${
                              num === settings.maxMenteesPerMentor 
                                ? 'font-bold text-brand-500' 
                                : 'text-gray-400 dark:text-gray-600'
                            }`}
                            onClick={() => updateSettings('maxMenteesPerMentor', num)}
                          >
                            {num}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex justify-between mt-3 text-xs text-gray-500 dark:text-gray-400">
                      <span>1:1 Focus</span>
                      <span>Group Scale</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>


          {/* Additional Settings */}
          <div>
            <h5 className="text-lg font-semibold text-navy-700 dark:text-white mb-4">
              Matching & Automation
            </h5>
            
            <div className="space-y-4">
              {/* Multiple Mentors Option - only show for 1:1 */}
              {settings.maxMenteesPerMentor === 1 && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h6 className="font-semibold text-navy-700 dark:text-white mb-1">
                        Allow Multiple Mentors
                      </h6>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Override 1:1 limit - mentees keep their assigned mentor while having additional recommended mentors for different skill areas (up to 10 total). Uses intelligent matching algorithms.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.allowMultipleMentors}
                        onChange={(e) => updateSettings('allowMultipleMentors', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-300 dark:peer-focus:ring-brand-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-brand-500"></div>
                    </label>
                  </div>
                </div>
              )}

              {/* Auto-Matching */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h6 className="font-semibold text-navy-700 dark:text-white mb-1">
                      Auto Assignment
                    </h6>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {settings.maxMenteesPerMentor === 1 
                        ? "Automatically matches mentees with mentors based on skills and interests. When disabled, requires manual pairing."
                        : "Required for group mentorship - automatically assigns mentees to available mentors"
                      }
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.maxMenteesPerMentor > 1 ? true : settings.enableAutoMatching}
                      disabled={settings.maxMenteesPerMentor > 1}
                      onChange={(e) => updateSettings('enableAutoMatching', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 rounded-full peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-300 dark:peer-focus:ring-brand-800 peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-brand-500 ${
                      settings.maxMenteesPerMentor > 1 
                        ? 'bg-brand-500 opacity-50 cursor-not-allowed' 
                        : 'bg-gray-200'
                    }`}></div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSaveSettings}
              disabled={isSaving}
              className={`flex items-center gap-2 rounded-lg px-6 py-3 font-medium transition-all ${
                isSaving
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-brand-500 text-white hover:bg-brand-600'
              }`}
            >
              <MdSave className="h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MentorshipSettingsPage;